/**
 * How a figure is read against a limit it prints the same as (OR-19 stopgap, round 29 class M): the limit is tested on the exact figure, the player reads the printed one, so a
 * reading that prints as the limit but is past it gets one more decimal and "just past", and one that equals the limit is "at your limit: not a breach". Every other pair prints as `formatKpi` does.
 */
import { KPI_DEFS, type KpiId } from "@bsim/domain";
import { formatMetric } from "@gxs/metric-defs";
import { formatKpi } from "../format";

export interface LimitReading {
  /** The reading and the limit as printed (both to the extra decimal when the pair needs it). */
  readonly value: string;
  readonly limit: string;
  /** "just past" or "at your limit: not a breach"; null when the printed figures already tell the story. */
  readonly note: string | null;
}

const decimalsOf = (text: string): number => /\.(\d+)/.exec(text)?.[1]?.length ?? 0;

/** `past`: whether the exact figure is on the wrong side of the limit (the caller knows the direction of its limit). */
export function limitReading(kpi: KpiId, value: number, limit: number, past: boolean): LimitReading {
  const plain = { value: formatKpi(kpi, value), limit: formatKpi(kpi, limit), note: null };
  if (value === limit) return { ...plain, note: "at your limit: not a breach" };
  if (!past || plain.value !== plain.limit) return plain;
  const unit = KPI_DEFS[kpi].unit;
  // Add decimals until the two figures differ (at most four more): the reading is then printed apart from its limit.
  for (let extra = 1; extra <= 4; extra++) {
    const precision = decimalsOf(plain.value) + extra;
    const [v, l] = [formatMetric(value, unit, { precision }), formatMetric(limit, unit, { precision })];
    if (v !== l || extra === 4) return { value: v, limit: l, note: "just beyond" };
  }
  return plain;
}
